import {
  ArrowLeft,
  Bell,
  Building,
  Calendar,
  Clock,
  CreditCard,
  Edit,
  HardDrive,
  Mail,
  MessageSquare,
  Phone,
  Shield,
  User,
  Users,
  Users2,
} from "lucide-react";
import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { getAllEmployee, setUserInformation } from "../../../../services/apiservices/employee";
import { getIndustriesList } from "../../../../services/apiservices/supplies";
import { Button } from "../../../../components/ui/button";
import AuthContext from "../../../../hooks/authContext";
import moment from "moment";
import { CancelStripeSubscription, findSubscriptionByCustomerId } from "../../../../services/apiservices/users";
import { getCompanyStorageData } from "../../../../services/apiservices/messages";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../components/ui/table";
import { packageAccess } from "../../../../services/helpers";
import { Progress } from "../../../../components/ui/progrss";
import { fetchCompanyNotifications, filterAndGroupNotifications } from "../../../../services/apiservices/notifications";
import CommonContext from "../../../../hooks/commonContext";
import { forgotPasswordUser } from "../../../../services/firebase";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../../../../components/ui/alertDialog";
import { useToast } from "../../../../hooks/useToast";
import { handleEmailChangeSuperUser } from "../../../../services/apiservices/email";
import { Input } from "../../../../components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../../../../components/ui/dialog";

const formatStorage = (bytes) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1 * 1024 * 1024 * 1024 * 1024) {
    return `${(bytes / (1 * 1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`;
  }
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const StorageUsageGauge = ({ 
  used, 
  total, 
  percentage 
}) => {
  const getProgressColor = () => {
    if (percentage > 90) return "bg-red-500";
    if (percentage > 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="w-full space-y-2">
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm font-medium">
          {formatStorage(used)} of {formatStorage(total)} used
        </span>
        <span className="text-sm text-gray-500">
          {percentage < 0.01 ? "0.01%" : `${percentage.toFixed(2)}%`}
        </span>
      </div>
      <Progress
        value={percentage} 
        className="h-2 bg-gray-200 rounded-full"
        indicatorClassName={getProgressColor()}
      />
    </div>
  );
};

const AccountDetails = () => {
  const [companyData, setCompanyData] = useState();
  const [editInformationDailog, setEditInformationDailog] = useState(false);
  const [companyUserData, setCompanyUserData] = useState();
  const [storageData, setStorageData] = useState({totalStorage: null, percentage: null, usedStorage: null, images: null, document: null, others: null });
  const [notificationData, setNotificationData] = useState({groupedBy7Days: null, groupedBy30Days: null, groupedBy90Days: null});
  const [messageTimeframe, setMessageTimeframe] = useState('30days');
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const { accountId } = useParams();
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const commonContext = useContext(CommonContext)
  const { toast } = useToast();
  const [step, setStep] = useState(1);

  const formatDate = (timestamp) =>
    timestamp ? moment.unix(timestamp).format("MM/DD/YYYY") : "N/A";
  const formatStripeAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const fetchDetails = async () => {
    commonContext?.setLoader(true);
    const [companyUsersRes, industriesRes] = await Promise.all([
      new Promise((resolve, reject) => getAllEmployee(resolve, reject)),
      new Promise((resolve, reject) =>
        getIndustriesList({}, (res) =>
          res ? resolve(res) : reject("Failed to fetch industries")
        )
      ),
    ]);

    const companyData = companyUsersRes?.data?.filter(
      (item) => item.id === accountId
    );
    const customerId = companyData?.[0]?.customer;
    const companyId = companyData?.[0]?.id;

    const [subscriptionRes, storageRes, notificationRes] = await Promise.all([
      findSubscriptionByCustomerId(customerId),
      getCompanyStorageData(companyId),
      fetchCompanyNotifications(companyId)
    ]);


    const groupedBy7Days = filterAndGroupNotifications(notificationRes?.data, 7);
    const groupedBy30Days = filterAndGroupNotifications(notificationRes?.data, 30);
    const groupedBy90Days = filterAndGroupNotifications(notificationRes?.data, 90); 
    setNotificationData({groupedBy7Days: groupedBy7Days, groupedBy30Days: groupedBy30Days, groupedBy90Days: groupedBy90Days})

    const companyUsersList = companyUsersRes?.data?.filter((item) => item?.companyId === companyId);
    const product = companyData?.[0]?.subscriptions?.data?.length > 0 ? companyData?.[0]?.subscriptions?.data?.[0]?.plan?.product : subscriptionRes?.data?.[0]?.plan?.product
    const userPromocode = auth?.userProfile?.userPromoCode || auth?.userProfile?.companyData?.userPromoCode;
    const subscriptionId = auth?.userProfile?.subscriptionDetails?.scheduleId || auth?.userProfile?.subscriptions?.data?.[0]?.scheduleId;
    const allowedPermissions = packageAccess(product, userPromocode, subscriptionId);
    const extraLimit =  companyData?.[0]?.subscriptions?.additionalQuantity ? companyData?.[0]?.subscriptions?.additionalQuantity * 5 : 0 ;
    const extraLimitInBytes = extraLimit * 1024 ** 3;
    const storageLimit = (allowedPermissions?.storageLimits ?? 0) + extraLimitInBytes;
    const usedPercentage = (storageRes?.totalSize / storageLimit) * 100;
    setStorageData({totalStorage: storageLimit, usedStorage: storageRes?.totalSize, percentage: usedPercentage, images: storageRes?.images, document: storageRes?.documents, others: storageRes?.others});

    setCompanyUserData(companyUsersList)

    const newData = companyData?.map((item) => {
      const industry = industriesRes?.find((ind) => ind?.id === item?.industry);
      const activeSub = item?.subscriptions?.data?.length > 0 ? item?.subscriptions?.data?.[0] : false;
      const subscriptionDetails = subscriptionRes?.data?.[0];

      return {
        ...item,
        // name: `${item?.firstName} ${item?.lastName}`,
        name: item?.companyName,
        email: item?.email,
        industry: industry?.title || "N/A",
        type: item?.role === "companyUser" ? "Company Manager" : item.role,
        planName: activeSub ? activeSub?.plan?.name : subscriptionDetails?.status,
        subscriptionsStatus: activeSub ? activeSub?.status : subscriptionDetails?.status,
        interval: activeSub ? activeSub?.plan?.interval : subscriptionDetails?.plan?.interval,
        amount: formatStripeAmount(activeSub ? activeSub?.plan?.amount : subscriptionDetails?.plan?.amount),
        nextBillingDate: formatDate(activeSub ? activeSub?.current_period_end : subscriptionDetails?.current_period_end),
        lastBillingDate: formatDate(activeSub ? activeSub?.current_period_start : subscriptionDetails?.current_period_start),
        subscriptionDetails: subscriptionDetails
      };
    });

    setCompanyData(newData?.[0]);
    commonContext?.setLoader(false);
  };

  useEffect(() => {
    fetchDetails();
  }, []);

  const mockMessageStats = {
    "7days": {
      sms: notificationData?.groupedBy7Days?.SMS?.length,
      email: notificationData?.groupedBy7Days?.Email?.length,
      push: notificationData?.groupedBy7Days?.PushNotification?.length,
    },
    "30days": {
      sms: notificationData?.groupedBy30Days?.SMS?.length,
      email: notificationData?.groupedBy30Days?.Email?.length,
      push: notificationData?.groupedBy30Days?.PushNotification?.length,
    },
    "90days": {
      sms: notificationData?.groupedBy90Days?.SMS?.length,
      email: notificationData?.groupedBy90Days?.Email?.length,
      push: notificationData?.groupedBy90Days?.PushNotification?.length,
    },
  };

  const handleResetPassword = async () => {
    const email = companyData?.email;
    await forgotPasswordUser(email, () => {});
    toast({
      title: "Password Reset Email Sent",
      description: `A password reset email has been sent to ${email}`,
    });
    setResetPasswordDialog(false);
  };

  const resetAndClose = () => {
    setStep(1);
  };

  const handleSuspendAccount = async () => {
    commonContext?.setLoader(true);

    try {
      const customerId = companyData?.customer;
      const subscriptionId = companyData?.subscriptions?.data?.[0]?.id || companyData?.subscriptionDetails?.id;

      const formData = {
        feedBack: "Deleted By SuperUser",
        selectedOption: "Missing features"
      };

      await CancelStripeSubscription(subscriptionId, customerId, formData);

      toast({
        title: "Account Suspended",
        description: `${companyData?.name}'s account has been suspended.`,
        variant: "destructive",
      });

      await fetchDetails();
    } catch (error) {
      console.error("Failed to suspend account:", error);
      toast({
        title: "Error",
        description: "Failed to suspend the account. Please try again.",
        variant: "destructive",
      });
    } finally {
      commonContext?.setLoader(false);
    }
  };

  const handleOpenEdit = () => {
    setFormData({
      firstName: companyData?.firstName || "",
      lastName: companyData?.lastName || "",
      email: companyData?.email || "",
      contact: companyData?.contact || "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateInformation = async () => {
    try {
      setEditInformationDailog(false);
      commonContext?.setLoader(true);

      const userId = companyData?.id;
      const data = {
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        phoneNumber: formData?.contact,
      };

      let mobileChange = false;

      if(companyData?.contact !== formData?.contact) {
        mobileChange = true;
      }

      if (companyData?.email !== formData?.email) {
        data.pendingEmail = formData?.email;
        data.email = companyData?.email;

        handleEmailChangeSuperUser(
          companyData?.email,
          formData?.email,
          data?.firstName,
          userId
        );
      } else {
        data.email = companyData?.email;
      }

    const result = await setUserInformation(userId, data, mobileChange);

    if (result.status === true) {
      await fetchDetails();
      toast({
        title: "Account Updated",
        description: `${companyData?.name}'s account has been updated successfully.`,
      });
    } else if (result.field === "phone") {
      toast({
        title: "Account Updated Error",
        description: "This phone number is already in use by another account.",
        variant: "destructive",
      });
      setFormData({
        firstName: formData?.firstName || "",
        lastName: formData?.lastName || "",
        email: formData?.email || "",
        contact: formData?.contact || "",
      });
      setEditInformationDailog(true);
    } else {
      toast({
        title: "Update Failed",
        description:
          result.message || "Something went wrong while updating the account.",
        variant: "destructive",
      });
    }
    } catch (error) {
      console.error("Error updating information:", error);
      toast({
        title: "Update Failed",
        description: "Something went wrong while updating the account.",
        variant: "destructive",
      });
    } finally {
      commonContext?.setLoader(false);
    }
  };
  

  const getRoleBadgeColor = (role) => {
    const roles = Array.isArray(role) ? role : [role];

    if (roles.includes("admin")) return "bg-green-500 hover:bg-green-600";
    if (roles.includes("Company Manager") || roles.includes("companyUser"))
      return "bg-blue-500 hover:bg-blue-600";
    if (roles.includes("user")) return "bg-gray-500 hover:bg-gray-600";

    return "bg-slate-500 hover:bg-slate-600";
  };

  const handleNaviage = () => {
    navigate("/accounts");
  };


  const fileTypes = [
    { name: "Documents", ...storageData.document },
    { name: "Images", ...storageData.images },
    { name: "Others", ...storageData.others }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-16 md:mb-24">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleNaviage()}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-16"
        >
          <ArrowLeft className="mr-8 h-4 w-4" />
          <span className="text-sm md:text-base">Back to Accounts</span>
        </Button>
        <h1 className="text-xl md:text-2xl font-bold text-foreground">Account Details</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="bg-white dark:bg-[#2c3440] lg:col-span-1 p-24">
          <CardHeader className="pb-16">
            <CardTitle className="text-base md:text-lg">
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-8">
            <div className="flex items-center mb-24 md:mb-32">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-200 dark:bg-slate-600 dark:text-white rounded-full flex items-center justify-center text-lg md:text-2xl font-bold text-slate-600 mr-16 flex-shrink-0">
                {companyData?.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg md:text-xl font-medium truncate mb-8">
                  {companyData?.name}
                </h3>
                <Badge
                  style={{ borderRadius: "6px" }}
                  className={`${getRoleBadgeColor(
                    companyData?.type || ""
                  )} text-xs`}
                  variant={"info"}
                >
                  {companyData?.type?.charAt(0).toUpperCase() +
                    companyData?.type?.slice(1)}
                </Badge>
              </div>
            </div>

            <div className="space-y-5">
              <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Email Address
                    </p>
                    <a
                      href={`mailto:${companyData?.email}`}
                      className="text-blue-600 hover:underline text-sm break-all"
                      style={{ color: "#2563EB" }}
                    >
                      {companyData?.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                <div className="flex items-start">
                  <Phone className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Phone Number
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200">
                      {companyData?.phone || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                <div className="flex items-start">
                  <User className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Company
                    </p>
                    <p className="text-sm text-slate-800 break-words dark:text-slate-200">
                      {companyData?.name}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                <div className="flex items-start">
                  <Building className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Industry
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200">
                      {companyData?.industry}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                <div className="flex items-start">
                  <Users2 className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Company Size
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200">
                      {companyData?.peoples}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pb-2">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Access Level
                    </p>
                    <p className="text-sm text-slate-800 capitalize dark:text-slate-200">
                      {companyData?.type} Level Access
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#2c3440] lg:col-span-2 p-24">
          <CardHeader className="pb-16">
            <CardTitle className="text-base md:text-lg">
              Account Activity & Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-5">
                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Account Status
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        {companyData?.status === true ? "Active" : "Inactive"}
                      </p>
                    </div>
                    <Badge
                      className="bg-green-100 text-green-800 hover:bg-green-200 text-xs ml-8"
                      style={{ borderRadius: "6px" }}
                    >
                      {companyData?.status === true ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </div>

                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Last Login
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">Today, 09:30 AM</p>
                    </div>
                  </div>
                </div>

                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex items-start">
                    <Calendar className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Account Created
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        {moment(companyData?.createdAt).format("MMMM D, YYYY")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-5">
                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Subscription Plan
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        {companyData?.planName === "trialing" ? "Trial Plan" : companyData?.planName}
                      </p>
                    </div>
                    <Badge
                      className="bg-blue-100 text-blue-800 hover:bg-blue-200 text-xs ml-8"
                      style={{ borderRadius: "6px" }}
                    >
                      {companyData?.subscriptionsStatus === "trialing" ? "Trial Plan" : companyData?.subscriptionsStatus}
                    </Badge>
                  </div>
                </div>

                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Billing Cycle
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                        {companyData?.interval === "month" ? "Monthly" : "Yearly"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-b border-gray-100 dark:border-gray-700 pb-16">
                  <div className="flex items-start">
                    <CreditCard className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Last Payment
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                      {companyData?.subscriptionsStatus === "trialing" ? (
                        "14-Days Free Trial"
                      ) : (
                        `${companyData?.amount} on ${companyData?.lastBillingDate}`
                      )}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pb-2">
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                        Next Payment
                      </p>
                      <p className="text-sm text-slate-800 dark:text-slate-200">
                      {companyData?.subscriptionsStatus === "trialing" ? (
                        "14-Days Free Trial"
                      ) : (
                        `${companyData?.amount} on ${companyData?.nextBillingDate}`
                      )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

          <Card className="bg-white dark:bg-[#2c3440] lg:col-span-3">
            <CardHeader className="flex flex-row items-center justify-between pb-16 ph-24 pt-24">
              <div>
                <CardTitle className="flex items-center text-base md:text-lg">
                  <Users className="mr-8 h-4 w-4 md:h-5 md:w-5" />
                  Team Members
                </CardTitle>
                <p className="text-xs md:text-sm text-muted-foreground mt-4">
                  Users active in this account
                </p>
              </div>
              <Badge variant="outline" className="ml-8 text-xs">
                {companyUserData?.length} Users
              </Badge>
            </CardHeader>
            <CardContent className="ph-24 pb-24 pt-0">
              {/* Mobile View - Cards */}
              <div className="md:hidden space-y-4">
                {companyUserData?.map((member) => (
                  <Card key={member?.id} className="p-16 border border-gray-200 dark:border-gray-700">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <h4 className="font-medium text-sm">{member?.firstName} {member?.lastName}</h4>
                        <span className="text-xs text-gray-500 dark:bg-gray-800 px-2 py-1 rounded">
                          Added: {new Date(member?.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-12">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-4">Email</p>
                        <a href={`mailto:${member?.email}`} className="text-blue-600 hover:underline text-sm block break-all" style={{color:"blue"}}>
                          {member?.email}
                        </a>
                      </div>
                      <div className="border-t border-gray-100 dark:border-gray-700 pt-12">
                        <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-4">Last Active</p>
                        {/* <p className="text-sm text-gray-800 dark:text-gray-200">{formatDateTime(member.lastActive)}</p> */}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Desktop View - Table */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Date Added</TableHead>
                      <TableHead>Last Active</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {companyUserData?.map((member) => (
                      <TableRow key={member?.id} className="bg-white dark:bg-[#2c3440] dark:text-white">
                        <TableCell className="font-medium dark:text-white">{member?.firstName} {member?.lastName}</TableCell>
                        <TableCell className="max-w-[200px] truncate">
                          <a href={`mailto:${member?.email}`} className="text-blue-600 hover:underline truncate block" style={{color:"blue"}}>
                            {member?.email}
                          </a>
                        </TableCell>
                        <TableCell className="dark:text-white">{new Date(member?.createdAt).toLocaleDateString()}</TableCell>
                        {/* <TableCell>{formatDateTime(member.lastActive)}</TableCell> */}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#2c3440] lg:col-span-2 p-24">
            <CardHeader className="pb-16">
              <CardTitle className="flex items-center text-base md:text-lg">
                <HardDrive className="mr-8 h-4 w-4 md:h-5 md:w-5" />
                Storage Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-24">
                <StorageUsageGauge 
                  used={storageData?.usedStorage} 
                  total={storageData?.totalStorage} 
                  percentage={storageData?.percentage}
                />
              </div>
              
              <div className="mt-24">
                <h4 className="font-medium mb-16 text-sm md:text-base">Storage Breakdown</h4>
                <div className="space-y-4">
                  {fileTypes?.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-12 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            index === 0
                              ? "bg-blue-500"
                              : index === 1
                              ? "bg-green-500"
                              : "bg-orange-500"
                          } mr-12`}
                        ></div>
                        <span className="text-sm font-medium">{item?.name}</span>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {(item?.size / (1024 * 1024 * 1024))?.toFixed(2)} GB
                        </span>
                        <span className="text-xs text-gray-500">{item?.count} files</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

        <Card className="bg-white dark:bg-[#2c3440] lg:col-span-1 p-24">
            <CardHeader className="pb-16">
              <CardTitle className="text-base md:text-lg">Message Statistics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center mb-24">
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                  <Button
                    variant={messageTimeframe === '7days' ? 'default' : 'ghost'} 
                    className="px-3 py-1 text-xs rounded-md"
                    onClick={() => setMessageTimeframe('7days')}
                  >
                    7 Days
                  </Button>
                  <Button 
                    variant={messageTimeframe === '30days' ? 'default' : 'ghost'} 
                    className="px-3 py-1 text-xs rounded-md"
                    onClick={() => setMessageTimeframe('30days')}
                  >
                    30 Days
                  </Button>
                  <Button 
                    variant={messageTimeframe === '90days' ? 'default' : 'ghost'}
                    className="px-3 py-1 text-xs rounded-md"
                    onClick={() => setMessageTimeframe('90days')}
                  >
                    90 Days
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center p-16 bg-blue-50 dark:bg-blue-900/20 rounded-lg border">
                  <div className="flex items-center">
                    <div className="p-8 bg-blue-100 text-blue-600 rounded-lg mr-12">
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">SMS Sent</p>
                      <p className="font-bold text-lg">{mockMessageStats[messageTimeframe]?.sms?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center p-16 bg-green-50 dark:bg-green-900/20 rounded-lg border">
                  <div className="flex items-center">
                    <div className="p-8 bg-green-100 text-green-600 rounded-lg mr-12">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Emails Sent</p>
                      <p className="font-bold text-lg">{mockMessageStats[messageTimeframe]?.email?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center p-16 bg-orange-50 dark:bg-orange-900/20 rounded-lg border">
                  <div className="flex items-center">
                    <div className="p-8 bg-orange-100 text-orange-600 rounded-lg mr-12">
                      <Bell className="w-4 h-4" />
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-300">Push Notifications</p>
                      <p className="font-bold text-lg">{mockMessageStats[messageTimeframe]?.push?.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white dark:bg-[#2c3440] lg:col-span-3 p-24">
            <CardHeader className="pb-16">
              <CardTitle className="text-base md:text-lg">Account Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Reset Password with Confirmation */}
                <AlertDialog open={resetPasswordDialog} onOpenChange={setResetPasswordDialog}>
                  <AlertDialogTrigger asChild>
                    <Button className="w-full h-12 text-sm" disabled={companyData?.selectedOption}>
                      Reset Password
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="mx-4">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-base md:text-lg">Reset User Password</AlertDialogTitle>
                      <AlertDialogDescription className="text-sm md:text-base">
                        This will send a password reset email to {companyData?.email}. The user will need to click the link in the email to create a new password.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                      <AlertDialogCancel className="w-full sm:w-auto">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleResetPassword} className="w-full sm:w-auto">Send Reset Email</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                
              {/* Edit Information */}
                <Dialog
                  open={editInformationDailog}
                  onOpenChange={setEditInformationDailog}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full h-12 text-sm"  onClick={handleOpenEdit}>
                      <Edit className="mr-8 h-4 w-4" />
                      Edit Information
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="mx-4 dark:bg-[#1A1F2C]">
                    <DialogHeader className={"dark:bg-[#1A1F2C] dark:text-white"}>
                      <DialogTitle className="text-base md:text-lg dark:text-white">
                        Change User Information
                      </DialogTitle>
                      <DialogDescription className="text-sm md:text-base dark:text-white">
                        {`Edit ${companyData?.name} Information`}
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-16">
                      <div>
                        <label className="text-sm font-medium dark:text-white">First Name</label>
                        <Input
                          name="firstName"
                          value={formData?.firstName}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Last Name</label>
                        <Input
                          name="lastName"
                          value={formData?.lastName}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Email</label>
                        <Input
                          name="email"
                          type="email"
                          value={formData?.email}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Phone</label>
                        <Input
                          name="contact"
                          type="tel"
                          value={formData?.contact}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>
                    </div>
                      <DialogFooter className="flex-col sm:flex-row gap-2 dark:bg-[#1A1F2C]">
                        <Button
                          variant="outline"
                          onClick={() => setEditInformationDailog(false)}
                          className="w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpdateInformation}
                          className="w-full sm:w-auto"
                        >
                          Update Information
                        </Button>
                      </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Double Confirmation for Suspend Account */}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="w-full h-12 text-sm" disabled={companyData?.selectedOption}>
                    Suspend Account
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="mx-4">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="text-base md:text-lg">
                      {step === 1 ? "Suspend User Account" : "Final Confirmation"}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-sm md:text-base">
                      {step === 1 ? (
                        <>
                          Are you sure you want to suspend <strong>{companyData?.name}</strong>'s account? This will prevent them from logging in or accessing any resources.
                        </>
                      ) : (
                        <>
                          This action will immediately revoke access for <strong>{companyData?.name}</strong>. Please confirm you want to proceed with suspension.
                        </>
                      )}
                    </AlertDialogDescription>
                  </AlertDialogHeader>

                  <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                    <AlertDialogCancel
                      className="w-full sm:w-auto"
                      onClick={() => setStep(1)}
                    >
                      Cancel
                    </AlertDialogCancel>

                    {step === 1 ? (
                      <Button
                        variant="destructive"
                        className="w-full sm:w-auto"
                        onClick={() => setStep(2)}
                      >
                        Continue to Suspend
                      </Button>
                    ) : (
                      <AlertDialogAction
                        onClick={() => {
                          handleSuspendAccount();
                          resetAndClose();
                        }}
                        className="w-full sm:w-auto"
                      >
                        Confirm Suspension
                      </AlertDialogAction>
                    )}
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              </div>
            </CardContent>
          </Card>
      </div>
    </div>
  );
};

export default AccountDetails;
